
import { useEffect } from 'react';
import { errorTracker } from '@/utils/errorTracking';
import { unifiedPerformanceMonitor } from '@/utils/unifiedPerformanceMonitor';
import { errorReporter } from '@/utils/errorReporter';

interface ErrorMonitorProps {
  children: React.ReactNode;
}

export const ErrorMonitor = ({ children }: ErrorMonitorProps) => {
  useEffect(() => {
    // Initialize Sentry-like error reporter
    errorReporter.init();

    // Global error handler for unhandled JavaScript errors
    const handleGlobalError = (event: ErrorEvent) => {
      const ignoredErrors = [
        'Non-Error promise rejection captured',
        'ResizeObserver loop limit exceeded',
        'Script error',
        'Network request failed',
        'Loading chunk',
        'Loading CSS chunk',
        'facebook.com',
        'google-analytics.com',
        'googletagmanager.com'
      ];

      const shouldIgnore = ignoredErrors.some(ignored => 
        event.message?.includes(ignored) || event.error?.message?.includes(ignored)
      );

      if (!shouldIgnore && event.error) {
        unifiedPerformanceMonitor.recordError();
        errorTracker.logError(
          event.error, 
          `Global error at ${event.filename}:${event.lineno}:${event.colno}`
        );
        // Also capture in error reporter with correlation ID
        errorReporter.captureException(event.error, {
          tags: { source: 'global_error' },
          extra: { filename: event.filename, lineno: event.lineno, colno: event.colno }
        });
      }
    };

    // Global handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const ignoredRejections = [
        'AbortError',
        'NetworkError',
        'Failed to fetch',
        'The user aborted a request',
        'Loading chunk',
        'facebook.com',
        'google-analytics.com'
      ];

      const reason = event.reason?.message || event.reason?.toString() || 'Unknown rejection';
      const shouldIgnore = ignoredRejections.some(ignored => reason.includes(ignored));

      if (!shouldIgnore) {
        const error = event.reason instanceof Error 
          ? event.reason 
          : new Error(`Unhandled Promise Rejection: ${reason}`);
        
        unifiedPerformanceMonitor.recordError();
        errorTracker.logError(error, 'Unhandled Promise Rejection');
        errorReporter.captureException(error, {
          tags: { source: 'unhandled_rejection' }
        });
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    unifiedPerformanceMonitor.initialize();

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      unifiedPerformanceMonitor.cleanup();
      errorReporter.cleanup();
    };
  }, []);

  return <>{children}</>;
};
